import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { getRoddoAccountIdentity } from './accountLinking';
import { getLastAuthBridgeResult } from './authBridge';

export type RoddoSupportDiagnostics = {
  diagnosticId: string;
  platform: 'ios' | 'android' | 'web' | string;
  runtime: 'native' | 'web';
  appVersion: string;
  appBuild: string;
  accountType: 'guest' | 'registered' | 'signed_out';
  providers: string[];
  authBridge: 'ok' | 'needs_attention' | 'unknown' | 'not_applicable';
};

const DIAG_SESSION_KEY = 'roddo.support.diag.v1';

function clean(value: unknown, max = 80): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ').slice(0, max);
}

function providerLabel(provider: string): string {
  if (provider === 'google.com') return 'google';
  if (provider === 'apple.com') return 'apple';
  if (provider === 'password') return 'password';
  return clean(provider, 32).toLowerCase();
}

function getOrCreateDiagnosticId(): string {
  try {
    const existing = window.sessionStorage.getItem(DIAG_SESSION_KEY);
    if (existing) return existing;

    const random = (() => {
      try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID().replace(/-/g, '').slice(0, 10);
        }
      } catch {}
      return Math.random().toString(36).slice(2, 12);
    })();

    const next = `rd_${Date.now().toString(36)}_${random}`;
    window.sessionStorage.setItem(DIAG_SESSION_KEY, next);
    return next;
  } catch {
    return `rd_${Date.now().toString(36)}`;
  }
}

export async function getRoddoSupportDiagnostics(): Promise<RoddoSupportDiagnostics> {
  const isNative = (() => {
    try {
      return Capacitor.isNativePlatform();
    } catch {
      return false;
    }
  })();

  const platform = (() => {
    try {
      return clean(Capacitor.getPlatform?.() || (isNative ? 'native' : 'web'), 24) || 'web';
    } catch {
      return isNative ? 'native' : 'web';
    }
  })();

  let appVersion = clean((import.meta as any)?.env?.VITE_APP_VERSION || '', 32);
  let appBuild = clean(
    (import.meta as any)?.env?.VITE_APP_BUILD ||
      (import.meta as any)?.env?.VITE_BUILD_NUMBER ||
      '',
    32,
  );

  if (isNative) {
    try {
      const info = await CapacitorApp.getInfo();
      appVersion = clean(info?.version || appVersion || 'native', 32);
      appBuild = clean(info?.build || appBuild, 32);
    } catch {
      if (!appVersion) appVersion = 'native';
    }
  } else if (!appVersion) {
    appVersion = 'web';
  }

  const identity = getRoddoAccountIdentity();
  const providers = Array.from(
    new Set((identity.providers || []).map(providerLabel).filter(Boolean)),
  ).slice(0, 6);

  const accountType: RoddoSupportDiagnostics['accountType'] = !identity.uid
    ? 'signed_out'
    : identity.isGuest
      ? 'guest'
      : 'registered';

  const bridgeResult = getLastAuthBridgeResult();
  const authBridge: RoddoSupportDiagnostics['authBridge'] = !isNative
    ? 'not_applicable'
    : bridgeResult === true
      ? 'ok'
      : bridgeResult === false
        ? 'needs_attention'
        : 'unknown';

  return {
    diagnosticId: getOrCreateDiagnosticId(),
    platform,
    runtime: isNative ? 'native' : 'web',
    appVersion,
    appBuild,
    accountType,
    providers,
    authBridge,
  };
}

export function formatRoddoSupportDiagnostics(diag: RoddoSupportDiagnostics): string {
  const version = diag.appBuild
    ? `${diag.appVersion} (${diag.appBuild})`
    : diag.appVersion;

  return [
    `Diagnostic ID: ${diag.diagnosticId}`,
    `Platform: ${diag.platform}`,
    `Runtime: ${diag.runtime}`,
    `Version: ${version}`,
    `Account: ${diag.accountType}`,
    `Providers: ${diag.providers.length ? diag.providers.join(', ') : 'none'}`,
    `Auth bridge: ${diag.authBridge}`,
  ].join('\n');
}

export function supportDeviceInfo(diag: RoddoSupportDiagnostics): string {
  return [
    `diag=${diag.diagnosticId}`,
    `platform=${diag.platform}`,
    `runtime=${diag.runtime}`,
    `account=${diag.accountType}`,
    `providers=${diag.providers.length ? diag.providers.join('+') : 'none'}`,
    `auth_bridge=${diag.authBridge}`,
  ].join('; ');
}

export function supportVersionLabel(diag: RoddoSupportDiagnostics): string {
  return diag.appBuild
    ? `${diag.appVersion} (${diag.appBuild})`
    : diag.appVersion;
}
